import React, { useState, useEffect } from 'react';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { collection, addDoc, getDocs, getDoc, deleteDoc, doc } from 'firebase/firestore';
import { auth, db, storage } from '../../firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../assets/styles/ClientShared.css';

const Upload = () => {
  const [files, setFiles] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [user, setUser] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [loading, setLoading] = useState(true);

  const uploadCategories = [
    { id: 'logo', label: 'Logo (e.g., PNG, JPG)', description: 'Upload your business logo for branding.' },
    { id: 'services', label: 'Service/Product Pictures', description: 'Images of your services or products.' },
    { id: 'location', label: 'Business Location Pictures', description: 'Images of your physical location.' },
    { id: 'gallery', label: 'Gallery/Portfolio Pictures', description: 'Showcase images for your portfolio.' },
    { id: 'others', label: 'Other Relevant Images', description: 'Additional images you want to include.' },
  ];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        try {
          const userDoc = doc(db, 'users', currentUser.uid);
          const userSnapshot = await getDoc(userDoc);
          if (userSnapshot.exists()) {
            const userData = userSnapshot.data();
            setProjectId(userData.projectId || null);
          } else {
            toast.error('User document not found.');
          }
        } catch (error) {
          toast.error('Error fetching user data.');
          console.error('Error fetching user data:', error);
        }
      } else {
        toast.warn('User is not signed in.');
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchUploadedFiles = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }

      try {
        const projectDocRef = doc(db, 'projects', projectId);
        const uploadsCollectionRef = collection(projectDocRef, 'uploads');
        const querySnapshot = await getDocs(uploadsCollectionRef);

        if (querySnapshot.empty) {
          toast.info('No uploaded files found.');
          setUploadedFiles([]);
        } else {
          const filesList = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setUploadedFiles(filesList);
        }
      } catch (error) {
        toast.error('Error fetching uploaded files.');
        console.error('Error fetching uploaded files:', error);
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      fetchUploadedFiles();
    }
  }, [projectId]);

  const handleFileChange = (event, categoryId) => {
    const selectedFiles = Array.from(event.target.files);
    setFiles((prevFiles) => ({
      ...prevFiles,
      [categoryId]: selectedFiles,
    }));
  };

  const handleUpload = async (categoryId) => {
    if (!user || !projectId) {
      toast.error('User or project ID is not available.');
      return;
    }

    const uploadPromises = [];
    const categoryFiles = files[categoryId] || [];

    for (let file of categoryFiles) {
      const storageRef = ref(storage, `projects/${projectId}/uploads/${categoryId}/${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      const uploadPromise = new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          null,
          (error) => {
            toast.error(`Failed to upload ${file.name}.`);
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              const uploadsCollectionRef = collection(db, `projects/${projectId}/uploads`);
              const docRef = await addDoc(uploadsCollectionRef, {
                name: file.name,
                category: categoryId,
                url: downloadURL,
                createdAt: new Date(),
              });

              resolve({ id: docRef.id, name: file.name, url: downloadURL, category: categoryId });
              toast.success(`${file.name} uploaded successfully.`);
            } catch (error) {
              toast.error(`Failed to save ${file.name} to Firestore.`);
              reject(error);
            }
          }
        );
      });

      uploadPromises.push(uploadPromise);
    }

    try {
      const uploadedFilesData = await Promise.all(uploadPromises);
      setUploadedFiles((prevFiles) => [...prevFiles, ...uploadedFilesData]);
      setFiles((prevFiles) => ({
        ...prevFiles,
        [categoryId]: [],
      }));
    } catch (error) {
      console.error('Failed to upload files:', error);
    }
  };

  const handleDelete = async (fileId, fileName, categoryId) => {
    if (!user || !projectId) {
      toast.error('User or project ID is not available.');
      return;
    }

    try {
      const storageRef = ref(storage, `projects/${projectId}/uploads/${categoryId}/${fileName}`);
      await deleteObject(storageRef);

      const fileDocRef = doc(db, `projects/${projectId}/uploads`, fileId);
      await deleteDoc(fileDocRef);

      setUploadedFiles((prevFiles) => prevFiles.filter((file) => file.id !== fileId));
      toast.success(`${fileName} deleted successfully.`);
    } catch (error) {
      toast.error(`Failed to delete ${fileName}.`);
      console.error('Failed to delete file:', error);
    }
  };

  if (loading) {
    return <div className="cp-loading"><p>Loading uploads...</p></div>;
  }

  return (
    <div className="cp-page">
      <div className="cp-page-header">
        <p className="cp-eyebrow">Client Portal</p>
        <h1 className="cp-title">Upload Files</h1>
        <p className="cp-subtitle">Upload images and assets for your website by category.</p>
      </div>

      {/* Upload categories */}
      <div className="cp-section" style={{marginBottom:'20px'}}>
        <div className="cp-section-header"><h2 className="cp-section-title">Upload by Category</h2></div>
        <div className="cp-section-body">
          {uploadCategories.map(category => (
            <div key={category.id} style={{marginBottom:'20px',paddingBottom:'20px',borderBottom:'1px solid var(--border)'}}>
              <p style={{fontFamily:'var(--font-ui)',fontSize:'0.9375rem',fontWeight:600,color:'var(--text-primary)',margin:'0 0 4px'}}>{category.label}</p>
              <p style={{fontFamily:'var(--font-ui)',fontSize:'0.875rem',color:'var(--text-tertiary)',margin:'0 0 12px'}}>{category.description}</p>
              <div style={{display:'flex',gap:'10px',alignItems:'center',flexWrap:'wrap'}}>
                <input type="file" multiple onChange={e=>handleFileChange(e, category.id)} style={{fontFamily:'var(--font-ui)',fontSize:'0.875rem',color:'var(--text-secondary)'}}/>
                <button className="cp-btn-primary" style={{padding:'8px 18px',fontSize:'0.875rem'}} onClick={()=>handleUpload(category.id)}>Upload</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Uploaded files list */}
      <div className="cp-section" style={{marginBottom:'24px'}}>
        <div className="cp-section-header"><h2 className="cp-section-title">Uploaded Files</h2></div>
        <div className="cp-section-body">
          {uploadedFiles.length > 0 ? (
            <div className="cp-doc-list">
              {uploadedFiles.map(file => (
                <div key={file.id} className="cp-doc-item">
                  <span className="cp-doc-label">{file.category}: {file.name}</span>
                  <div style={{display:'flex',gap:'10px',alignItems:'center'}}>
                    <a href={file.url} target="_blank" rel="noopener noreferrer" className="cp-doc-link">View →</a>
                    <button onClick={()=>handleDelete(file.id, file.name, file.category)} style={{fontFamily:'var(--font-ui)',fontSize:'0.8125rem',color:'var(--accent)',background:'none',border:'none',cursor:'pointer',padding:0}}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          ) : <p style={{fontFamily:'var(--font-ui)',fontSize:'0.9375rem',color:'var(--text-tertiary)',margin:0}}>No files uploaded yet.</p>}
        </div>
      </div>
    </div>
  );
};

export default Upload;
